import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Checkbox } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useModalApplication } from '@/common/hooks/useModalApplication'
import {
  ActivateUserForm,
  ActivateUserSchema,
} from '../../../schemas/ActivateUserSchema'

type ActivationFormProps = {
  onActivateUser: () => void
  isPending: boolean
}

export function ActivationForm({
  onActivateUser,
  isPending,
}: ActivationFormProps) {
  const { t } = useTranslation(['login', 'common'])
  const { handleModal, resetModal } = useModalApplication()
  const hookForm = useForm<ActivateUserForm>({
    resolver: zodResolver(ActivateUserSchema),
    mode: 'onChange',
    defaultValues: { acceptTerms: false },
  })

  const watchedAcceptTerms = hookForm.watch('acceptTerms')

  return (
    <FormProvider {...hookForm}>
      <div className="flex flex-col gap-6">
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Info className="text-brand-600 size-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-brand-900 font-medium mb-4">
                {t('activate_user.terms.title')}
              </h3>
              <p className="text-brand-700 text-sm mb-6">
                {t('activate_user.terms.subtitle')}
              </p>
              <div>
                <button
                  onClick={() =>
                    handleModal({
                      title: t('common:app_connection.terms.title'),
                      description: (
                        <div className="bg-gray-50 p-4 rounded-md border max-h-[400px] overflow-y-auto">
                          <div className="text-sm text-gray-600 whitespace-pre-wrap">
                            {t('common:app_connection.terms.content')}
                          </div>
                        </div>
                      ),
                      confirmText: t('common:app_connection.buttons.close'),
                      showButtons: true,
                      onConfirm: resetModal,
                    })
                  }
                  className="text-brand-600 text-base font-medium hover:underline"
                >
                  {t('activate_user.read_terms_link')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Checkbox
            name="acceptTerms"
            text={t('activate_user.accept_terms_text')}
          />

          <Button.Root
            onClick={onActivateUser}
            disabled={!watchedAcceptTerms || isPending}
            isLoading={isPending}
            size="lg"
            block
          >
            {t('activate_user.activate_button')}
          </Button.Root>
        </div>
      </div>
    </FormProvider>
  )
}
